import {
  getRepository,
  getRepositoryTags,
  getRepositoryV2,
} from "@prismicio/editor-fields"
import { request } from "@prismicio/editor-support/Http"
import {
  invalidateFetcherData,
  preload,
  useRequest,
} from "@prismicio/editor-support/Suspense"
import { captureException } from "@sentry/react"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { z } from "zod"

import { queryClient } from "../components/providers/QueryProvider"
import { env } from "../env"
import { getRepositoryName } from "../routing"
import { baseUrl as coreApiBaseUrl } from "./core"

export function useRepository() {
  return useRequest(getRepository, [
    coreApiBaseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
  ])
}

export function preloadRepository() {
  preload(getRepository, [
    coreApiBaseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
  ])
}

export function setSimulatorURL(simulator_url: string) {
  const body = { simulator_url }
  return request(new URL("repository", coreApiBaseUrl), {
    method: "PATCH",
    body,
    okType: z.unknown(),
  })
}

export function useRepositoryTags() {
  return useRequest(getRepositoryTags, [
    coreApiBaseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
  ])
}

export function invalidateRepositoryTags() {
  invalidateFetcherData(getRepositoryTags)
}

const repositoryBaseUrl = env.PRISMIC_REPOSITORY_BASE_URL
const repository = getRepositoryName()
const authStrategy = env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY

async function fetchRepositoryV2() {
  try {
    return await getRepositoryV2(repositoryBaseUrl, repository, authStrategy)
  } catch (error) {
    captureException(error, {
      extra: { repositoryBaseUrl, repository, authStrategy },
    })
    return null
  }
}

export function useRepositoryV2() {
  return useSuspenseQuery({
    queryKey: ["repository"],
    queryFn: fetchRepositoryV2,
  })
}

export function prefetchRepositoryV2() {
  void queryClient.prefetchQuery(
    queryOptions({
      queryKey: ["repository"],
      queryFn: fetchRepositoryV2,
    }),
  )
}
